import { Pipe, PipeTransform } from '@angular/core';
import * as moment from 'moment';
import { TranslateService } from '@ngx-translate/core';
import { getUserUtcOffset } from '../shared/utils/helpers';

@Pipe({
  name: 'daysAgoOrLeft',
})
export class DaysAgoOrLeftPipe implements PipeTransform {
  currentLang: string =
    localStorage.getItem('lang') ??
    this.translateService.currentLang ??
    this.translateService.defaultLang ??
    'en';

  constructor(private readonly translateService: TranslateService) {}

  /**
   * @param date_time - The date to format
   * @param mode - 'ago' for past dates (e.g. "2 hours ago"), 'left' for future dates (e.g. "in 3 days")
   * @param showDateAfterDays - When set, show the actual date instead of relative time if the difference
   *   from now exceeds this many days. Omit or 0 to always use relative time.
   */
  transform(
    date_time: Date | string,
    mode: 'ago' | 'left' = 'ago',
    showDateAfterDays?: number,
    format?: string
  ): string {
    if (!date_time || date_time === '') {
      return '';
    }

    const offsetHours = getUserUtcOffset() ?? 0;
    const m = moment(date_time).add(offsetHours, 'hours').locale(this.currentLang);
    const now = moment();
    const daysDiff = Math.abs(m.diff(now, 'days', true));

    if (showDateAfterDays != null && showDateAfterDays > 0 && daysDiff >= showDateAfterDays) {
      return m.format(format ?? 'L');
    }

    // Future dates keep moment's default relative wording ("in X …"); only past
    // dates use the compact "now / 23 m ago" style (see common.relative_time i18n).
    if (mode === 'left') {
      return m.fromNow(true);
    }
    return this.formatShortAgo(m, now);
  }

  /** Compact past-relative label: "now", "23 m ago", "5 h ago", "2 d ago", … (localized). */
  private formatShortAgo(m: moment.Moment, now: moment.Moment): string {
    const seconds = now.diff(m, 'seconds');
    if (seconds < 0) return m.fromNow(); // future date → moment's localized "in X …"
    if (seconds < 45) return this.translateService.instant('common.relative_time.now');

    const minutes = now.diff(m, 'minutes');
    if (minutes < 60) return this.unit('minute', minutes);

    const hours = now.diff(m, 'hours');
    if (hours < 24) return this.unit('hour', hours);

    const days = now.diff(m, 'days');
    if (days < 7) return this.unit('day', days);

    const weeks = now.diff(m, 'weeks');
    if (weeks < 5) return this.unit('week', weeks);

    const months = now.diff(m, 'months');
    if (months < 12) return this.unit('month', months);

    return this.unit('year', now.diff(m, 'years'));
  }

  /** Resolve a unit label, choosing singular/plural and interpolating the count. */
  private unit(name: string, count: number): string {
    const n = Math.max(1, count);
    const key = `common.relative_time.${name}.${n === 1 ? 'one' : 'other'}`;
    return this.translateService.instant(key, { n });
  }
}
